// Sección CAMPAÑAS SMS — listado, editor, estimación previa y envío.
//
// ── Contexto honesto (léelo antes de tocar nada) ───────────────────────────
// Hoy la plataforma NO captura el teléfono del cliente: el checkout no lo
// pide. `buildAudience()` (backend/src/routes/sms.js) construye la audiencia
// a partir de los pagos del tenant y descarta a todo el que no tenga un
// teléfono normalizable, así que ahora mismo CUALQUIER campaña estima 0
// destinatarios. Esta pantalla no lo disimula: cuando la estimación devuelve
// 0 lo dice con todas las letras y deshabilita el envío. Cuando el checkout
// empiece a pedir el teléfono, esto empezará a dar números solo.
//
// ── Seguridad de la interfaz ───────────────────────────────────────────────
// Enviar cuesta dinero real y es IRREVERSIBLE. Por eso:
//   • "Enviar ahora" nunca es un solo clic: siempre pasa por un modal que
//     dice cuántos destinatarios y cuánto coste estimado.
//   • No se deja enviar sin estimación. Y la estimación del modal se vuelve a
//     pedir al backend (`estimateCampaign`) justo antes de confirmar, sobre la
//     campaña YA GUARDADA — que es exactamente lo que va a despachar el
//     backend. Así nadie envía con un cálculo viejo ni con cambios sin
//     guardar.
//   • Si cambias plantilla o tienda después de estimar, la estimación se
//     invalida y se avisa.
//   • Mientras el estado es "sending" se sondea el progreso y los botones que
//     podrían duplicar el envío quedan deshabilitados.
//
// ── Contrato con el backend ────────────────────────────────────────────────
// Todos los nombres de campo y los valores de estado están copiados de
// backend/src/routes/sms.js (campaignView / _estimate / campaignMetrics), no
// inventados. Estados: draft | scheduled | sending | sent | cancelled.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { G, fmt, curSym, getAppTz, fmtDay, fmtDayTime } from '../theme';
import { smsApi, shopsApi } from '../api';

// ═══════════════════════════════════════════════════════════════════════════
// Constantes espejo del backend
// ═══════════════════════════════════════════════════════════════════════════

// CAMPAIGN_STATUSES en routes/sms.js. El orden es el del filtro.
const STATUS_META = {
  draft:     { label: 'Borrador',   badge: 'badge-gray',   color: G.muted },
  scheduled: { label: 'Programada', badge: 'badge-blue',   color: G.blue },
  sending:   { label: 'Enviando',   badge: 'badge-orange', color: G.orange },
  sent:      { label: 'Enviada',    badge: 'badge-green',  color: G.green },
  cancelled: { label: 'Cancelada',  badge: 'badge-red',    color: G.red },
};
const STATUS_ORDER = ['draft', 'scheduled', 'sending', 'sent', 'cancelled'];

// MAX_RECIPIENTS en routes/sms.js — el backend corta la audiencia aquí.
const MAX_RECIPIENTS = 5000;

// Cada cuánto se refresca una campaña en curso.
const POLL_MS = 3000;

const COLS = [
  { key: 'name',         label: 'Nombre',        sortable: true },
  { key: 'templateName', label: 'Plantilla',     sortable: true },
  { key: 'shopName',     label: 'Tienda',        sortable: true,  off: true },
  { key: 'recipients',   label: 'Destinatarios', sortable: true,  num: true },
  { key: 'status',       label: 'Estado',        sortable: true },
  { key: 'sent',         label: 'Enviados',      sortable: false, num: true, off: true },
  { key: 'delivered',    label: 'Entregados',    sortable: false, num: true, off: true },
  { key: 'failed',       label: 'Fallidos',      sortable: false, num: true, off: true },
  { key: 'cost',         label: 'Coste',         sortable: false, num: true, off: true },
  { key: 'scheduledAt',  label: 'Programada',    sortable: true },
  { key: 'sentAt',       label: 'Enviada',       sortable: true },
  { key: 'createdAt',    label: 'Creada',        sortable: true },
];

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

const num = (n) => (Number(n) || 0).toLocaleString('es-ES');

/**
 * Dinero de SMS. OJO: `fmt()` interpreta el importe en EUR y lo convierte a la
 * divisa de visualización del operador. El coste de SMS viene en la divisa que
 * el tenant configuró en su proveedor (`pricePerSegment` + `currency`), que no
 * tiene por qué ser EUR. Sólo delegamos en `fmt()` cuando coinciden; si no,
 * mostramos la divisa real del proveedor sin convertir nada — mejor un número
 * honesto en su divisa que uno convertido con un tipo de cambio que no aplica.
 */
function money(amount, currency) {
  const n = Number(amount);
  if (!isFinite(n)) return '--';
  const cur = String(currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return fmt(n);
  return `${curSym(cur)}${n.toFixed(2)}`;
}

/** Coste que sólo se muestra si el tenant configuró precio por segmento. */
function costOrDash(amount, currency, pricingConfigured) {
  if (pricingConfigured === false) return '--';
  return money(amount, currency);
}

/** ISO → valor para <input type="datetime-local"> en hora local del navegador. */
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Valor de datetime-local → ISO absoluto (el backend hace new Date(...)). */
function localInputToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** "17/09/2026 14:30" en la zona configurada del panel. */
function fmtWhen(iso) {
  if (!iso) return '--';
  const { day, time } = fmtDayTime(iso);
  if (day === '--') return '--';
  return time ? `${day} ${time}` : day;
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || '--', badge: 'badge-gray' };
  return <span className={`badge ${meta.badge}`}>{meta.label}</span>;
}

/**
 * Traduce los códigos de error del backend (providerBlockReason, lastError) a
 * algo que el operador entienda. Si no reconocemos el código, se muestra el
 * texto original: preferimos un mensaje feo pero verdadero a uno bonito y
 * genérico que esconda la causa.
 */
function humanError(raw) {
  if (!raw) return '';
  const s = String(raw);
  const code = s.split(':')[0];
  const map = {
    PROVIDER_NOT_CONFIGURED: 'No hay proveedor de SMS configurado en esta cuenta. Configúralo en Ajustes de SMS antes de enviar.',
    PROVIDER_DISABLED: 'El proveedor de SMS está desactivado o sin API key.',
    PROVIDER_MISCONFIGURED: 'Al proveedor de SMS le falta el remitente (sender ID / número).',
    PROVIDER_UNSUPPORTED: 'El proveedor configurado no está soportado.',
    PROVIDER_TIMEOUT: 'El proveedor de SMS no respondió a tiempo.',
    PROVIDER_NETWORK_ERROR: 'Error de red al contactar con el proveedor de SMS.',
    TEMPLATE_MISSING: 'La plantilla de la campaña ya no existe.',
    ALL_FAILED: 'Fallaron TODOS los destinatarios. Mira el historial de SMS para ver el error del proveedor.',
    DISPATCH_ERROR: 'El envío se interrumpió por un error interno.',
  };
  return map[code] || s;
}

/** Mensaje de error de axios, sin tragarse el detalle del backend. */
function apiError(e, fallback) {
  const msg = e && e.response && e.response.data && e.response.data.error;
  return msg ? humanError(msg) : fallback;
}

// ═══════════════════════════════════════════════════════════════════════════
// Aviso permanente: todavía no se capturan teléfonos
// ═══════════════════════════════════════════════════════════════════════════

function NoPhonesNotice({ reachable }) {
  // Sólo con el dato real: si aún no se sabe (null) o hay destinatarios, nada.
  if (reachable !== 0) return null;
  return (
    <div
      className="card"
      style={{ borderColor: G.borderGold, background: G.goldDim, marginBottom: 18 }}
    >
      <div className="card-body" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 20, lineHeight: 1 }}>⚠</div>
        <div style={{ fontSize: 13, color: G.white, lineHeight: 1.55 }}>
          <strong style={{ color: G.goldLight }}>Ningún cliente tiene teléfono guardado.</strong>{' '}
          La audiencia de una campaña se construye con los clientes que tienen teléfono, así que
          ahora mismo cualquier estimación saldrá en <strong>0 destinatarios</strong>. Puedes crear
          y guardar campañas —quedarán listas—, pero no se podrá enviar ninguna hasta que el
          checkout capture el teléfono.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bloque de estimación
// ═══════════════════════════════════════════════════════════════════════════

function EstimateBlock({ estimate, stale, loading, onEstimate, canEstimate }) {
  const zero = estimate && estimate.recipients === 0;

  return (
    <div
      style={{
        border: `1px solid ${stale ? G.orange : G.border}`,
        borderRadius: 10,
        padding: 16,
        background: G.surface,
        marginTop: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: G.dim }}>
          Estimación
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={onEstimate}
          disabled={loading || !canEstimate}
          title={!canEstimate ? 'Elige primero una plantilla' : undefined}
        >
          {loading ? 'Calculando…' : estimate ? '↻ Volver a estimar' : 'Previsualizar estimación'}
        </button>
      </div>

      {stale && (
        <div style={{ marginTop: 10, fontSize: 12, color: G.orange, fontWeight: 600 }}>
          Los filtros han cambiado, vuelve a estimar.
        </div>
      )}

      {!estimate && !loading && (
        <div style={{ marginTop: 12, fontSize: 12, color: G.muted }}>
          Sin estimar. Pulsa <em>Previsualizar estimación</em> para saber a cuánta gente llegaría y
          cuánto costaría, antes de enviar nada.
        </div>
      )}

      {estimate && (
        <>
          <div
            style={{
              marginTop: 14,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              opacity: stale ? 0.45 : 1,
            }}
          >
            <EstimateCell
              label="Destinatarios"
              value={num(estimate.recipients)}
              color={zero ? G.red : G.white}
            />
            <EstimateCell
              label="Segmentos por mensaje"
              value={estimate.segmentsPerMessage != null ? String(estimate.segmentsPerMessage) : '--'}
              hint={
                estimate.maxSegmentsPerMessage && estimate.maxSegmentsPerMessage !== estimate.segmentsPerMessage
                  ? `máx. ${estimate.maxSegmentsPerMessage}`
                  : ''
              }
            />
            <EstimateCell label="Segmentos totales" value={num(estimate.totalSegments)} />
            <EstimateCell
              label="Coste estimado"
              value={costOrDash(estimate.estimatedCost, estimate.currency, estimate.pricingConfigured)}
              hint={
                estimate.pricingConfigured === false
                  ? 'sin precio configurado'
                  : `${money(estimate.pricePerSegment, estimate.currency)} / segmento`
              }
              color={estimate.pricingConfigured === false ? G.muted : G.goldLight}
            />
          </div>

          {zero && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: G.redDim,
                border: `1px solid ${G.red}40`,
                fontSize: 12.5,
                color: G.white,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: G.red }}>0 destinatarios:</strong> ningún cliente tiene teléfono
              guardado. Esta campaña no se puede enviar.
            </div>
          )}

          {estimate.pricingConfigured === false && !zero && (
            <div style={{ marginTop: 12, fontSize: 12, color: G.muted, lineHeight: 1.5 }}>
              No hay precio por segmento configurado en el proveedor, así que el coste no se puede
              calcular. Se muestra "--" en lugar de un importe inventado.
            </div>
          )}

          {estimate.capped && (
            <div style={{ marginTop: 12, fontSize: 12, color: G.orange, lineHeight: 1.5 }}>
              La audiencia real es de {num(estimate.audienceTotal)} personas, pero el backend corta en{' '}
              {num(estimate.maxRecipients || MAX_RECIPIENTS)} por campaña. Sólo se enviará a los
              primeros {num(estimate.recipients)}.
            </div>
          )}

          {estimate.unicodeMessages > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: G.dim, lineHeight: 1.5 }}>
              {num(estimate.unicodeMessages)} mensaje(s) pasan a Unicode (UCS-2) al sustituir las
              variables — caben 70 caracteres por segmento en vez de 160, y por eso cuestan más.
            </div>
          )}

          {estimate.providerReady === false && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 8,
                background: G.redDim,
                border: `1px solid ${G.red}40`,
                fontSize: 12.5,
                color: G.white,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: G.red }}>Proveedor no listo:</strong>{' '}
              {humanError(estimate.providerError)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EstimateCell({ label, value, hint, color }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.8, color: G.muted }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: 'DM Mono, monospace',
          fontSize: 20,
          fontWeight: 600,
          color: color || G.white,
          marginTop: 3,
        }}
      >
        {value}
      </div>
      {hint ? <div style={{ fontSize: 10.5, color: G.muted, marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Métricas de entrega de una campaña
// ═══════════════════════════════════════════════════════════════════════════

function MetricsBlock({ metrics, status }) {
  if (!metrics) return null;
  const attempted = Number(metrics.attempted) || 0;
  const total = Number(metrics.total) || 0;

  // Sin intentos todavía no hay nada que medir: no pintamos ceros que parezcan
  // un resultado real.
  if (!attempted && status !== 'sending') {
    return (
      <div style={{ marginTop: 4, fontSize: 12, color: G.muted }}>
        Sin datos de entrega todavía — esta campaña aún no ha enviado ningún mensaje.
      </div>
    );
  }

  const pct = total > 0 ? Math.min(100, Math.round((attempted / total) * 100)) : 0;

  return (
    <div style={{ marginTop: 4 }}>
      {status === 'sending' && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: G.dim, marginBottom: 5 }}>
            <span>Enviando…</span>
            <span style={{ fontFamily: 'DM Mono, monospace' }}>
              {num(attempted)} / {num(total)} ({pct}%)
            </span>
          </div>
          <div className="progress">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
        <EstimateCell label="Total" value={num(total)} />
        <EstimateCell label="Enviados" value={num(metrics.sent)} color={G.white} />
        <EstimateCell label="Entregados" value={num(metrics.delivered)} color={G.green} />
        <EstimateCell
          label="Fallidos"
          value={num(metrics.failed)}
          color={(Number(metrics.failed) || 0) > 0 ? G.red : G.white}
        />
        <EstimateCell
          label="Tasa de entrega"
          value={(Number(metrics.sent) || 0) > 0 ? `${metrics.deliveryRate}%` : '--'}
          hint={(Number(metrics.sent) || 0) > 0 ? 'entregados / enviados' : 'sin envíos'}
        />
        <EstimateCell
          label="Coste total"
          value={money(metrics.cost, metrics.currency)}
          hint={`${num(metrics.segments)} segmentos`}
          color={G.goldLight}
        />
      </div>

      {(Number(metrics.pending) || 0) > 0 && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: G.muted }}>
          {num(metrics.pending)} mensaje(s) todavía en estado pendiente.
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: G.muted, lineHeight: 1.5 }}>
        "Entregados" sólo sube cuando el proveedor confirma la entrega. Mientras no haya webhook de
        acuse (DLR) conectado, lo normal es ver todo como "enviado" y la tasa de entrega baja: no
        significa que el SMS no haya llegado.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de confirmación de envío — el guardarraíl principal
// ═══════════════════════════════════════════════════════════════════════════

function SendConfirmModal({ campaign, estimate, loading, busy, onCancel, onConfirm }) {
  const recipients = estimate ? Number(estimate.recipients) || 0 : 0;
  const zero = !!estimate && recipients === 0;
  const providerBad = !!estimate && estimate.providerReady === false;
  const canSend = !!estimate && !zero && !providerBad && !loading && !busy;

  return (
    <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="modal" style={{ width: 540 }}>
        <div className="modal-title">Enviar campaña ahora</div>

        <div style={{ fontSize: 13, color: G.dim, lineHeight: 1.6, marginBottom: 16 }}>
          Vas a enviar <strong style={{ color: G.white }}>{campaign.name || 'esta campaña'}</strong>.
          Los SMS <strong style={{ color: G.orange }}>se cobran y no se pueden deshacer</strong>: una
          vez enviados no hay vuelta atrás.
        </div>

        {loading && (
          <div style={{ fontSize: 13, color: G.muted, padding: '18px 0' }}>
            Recalculando la estimación sobre la campaña guardada…
          </div>
        )}

        {!loading && !estimate && (
          <div style={{ fontSize: 13, color: G.red, padding: '10px 0' }}>
            No se ha podido calcular la estimación. Sin estimación no se envía.
          </div>
        )}

        {!loading && estimate && (
          <div
            style={{
              border: `1px solid ${G.border}`,
              borderRadius: 10,
              padding: 16,
              background: G.surface,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
            }}
          >
            <EstimateCell
              label="Destinatarios"
              value={num(recipients)}
              color={zero ? G.red : G.goldLight}
            />
            <EstimateCell
              label="Coste estimado"
              value={costOrDash(estimate.estimatedCost, estimate.currency, estimate.pricingConfigured)}
              hint={estimate.pricingConfigured === false ? 'sin precio configurado' : `${num(estimate.totalSegments)} segmentos`}
              color={estimate.pricingConfigured === false ? G.muted : G.goldLight}
            />
          </div>
        )}

        {zero && (
          <div style={{ marginTop: 14, fontSize: 12.5, color: G.white, background: G.redDim, border: `1px solid ${G.red}40`, borderRadius: 8, padding: '10px 12px', lineHeight: 1.5 }}>
            <strong style={{ color: G.red }}>0 destinatarios:</strong> ningún cliente tiene teléfono
            guardado. No hay nada que enviar.
          </div>
        )}

        {providerBad && (
          <div style={{ marginTop: 14, fontSize: 12.5, color: G.white, background: G.redDim, border: `1px solid ${G.red}40`, borderRadius: 8, padding: '10px 12px', lineHeight: 1.5 }}>
            {humanError(estimate.providerError)}
          </div>
        )}

        {!loading && estimate && estimate.capped && (
          <div style={{ marginTop: 14, fontSize: 12, color: G.orange, lineHeight: 1.5 }}>
            Se enviará sólo a los primeros {num(recipients)} de {num(estimate.audienceTotal)}: el
            backend corta en {num(estimate.maxRecipients || MAX_RECIPIENTS)} por campaña.
          </div>
        )}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button
            className="btn btn-gold"
            onClick={onConfirm}
            disabled={!canSend}
            title={zero ? 'No hay destinatarios' : providerBad ? 'El proveedor de SMS no está listo' : undefined}
          >
            {busy ? 'Enviando…' : `Sí, enviar a ${num(recipients)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de cancelación
// ═══════════════════════════════════════════════════════════════════════════

function CancelConfirmModal({ campaign, busy, onClose, onConfirm }) {
  const sending = campaign.status === 'sending';
  const alreadySent = campaign.metrics ? Number(campaign.metrics.sent) || 0 : 0;

  return (
    <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal" style={{ width: 480 }}>
        <div className="modal-title">Cancelar campaña</div>
        <div style={{ fontSize: 13, color: G.dim, lineHeight: 1.6 }}>
          {sending ? (
            <>
              La campaña <strong style={{ color: G.white }}>{campaign.name}</strong> se está enviando
              ahora mismo. Al cancelar se detiene antes del siguiente destinatario.
            </>
          ) : (
            <>
              Se cancelará <strong style={{ color: G.white }}>{campaign.name}</strong>: dejará de
              estar programada y no se enviará.
            </>
          )}
        </div>

        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            borderRadius: 8,
            background: G.orangeDim,
            border: `1px solid ${G.orange}40`,
            fontSize: 12.5,
            color: G.white,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: G.orange }}>Cancelar no revierte lo ya enviado.</strong>{' '}
          {alreadySent > 0
            ? `Ya han salido ${num(alreadySent)} mensaje(s): esos están enviados y cobrados.`
            : 'Los mensajes que ya hayan salido siguen enviados y cobrados.'}
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Volver</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Cancelando…' : 'Cancelar campaña'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de borrado
// ═══════════════════════════════════════════════════════════════════════════

function DeleteConfirmModal({ campaign, busy, onClose, onConfirm }) {
  return (
    <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal" style={{ width: 440 }}>
        <div className="modal-title">Borrar campaña</div>
        <div style={{ fontSize: 13, color: G.dim, lineHeight: 1.6 }}>
          Se borrará <strong style={{ color: G.white }}>{campaign.name}</strong>. El historial de los
          SMS que ya se enviaron NO se borra: seguirá en Historial de SMS.
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Volver</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Borrando…' : 'Borrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Editor de campaña
// ═══════════════════════════════════════════════════════════════════════════

function CampaignEditor({ campaign, templates, shops, onClose, onSaved, showToast }) {
  const isNew = !campaign.id;

  const [form, setForm] = useState({
    name: campaign.name || '',
    templateId: campaign.templateId || '',
    shopId: campaign.shopId || '',
    scheduledAt: isoToLocalInput(campaign.scheduledAt),
  });
  // Copia de lo GUARDADO, para saber si hay cambios sin guardar.
  const [saved, setSaved] = useState({
    name: campaign.name || '',
    templateId: campaign.templateId || '',
    shopId: campaign.shopId || '',
    scheduledAt: isoToLocalInput(campaign.scheduledAt),
  });

  const [live, setLive] = useState(campaign);          // estado vivo (status, metrics…)
  const [estimate, setEstimate] = useState(null);
  const [estimateSig, setEstimateSig] = useState(null); // firma de los filtros al estimar
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEstimate, setSendEstimate] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const pollRef = useRef(null);

  const status = live.status || 'draft';
  const inFlight = status === 'sending';
  const finished = status === 'sent';
  const locked = inFlight || finished; // el backend rechaza editar en estos estados

  // Firma de los filtros que afectan a la audiencia/coste. Si cambia, la
  // estimación anterior deja de valer (mismo criterio que el competidor).
  const currentSig = `${form.templateId}|${form.shopId}`;
  const stale = !!estimate && estimateSig !== null && estimateSig !== currentSig;

  const dirty =
    form.name !== saved.name ||
    form.templateId !== saved.templateId ||
    form.shopId !== saved.shopId ||
    form.scheduledAt !== saved.scheduledAt;

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === form.templateId) || null,
    [templates, form.templateId]
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ── Sondeo mientras la campaña está enviando ─────────────────────────────
  useEffect(() => {
    if (!live.id || live.status !== 'sending') return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const fresh = await smsApi.getCampaign(live.id);
        if (alive && fresh) setLive(fresh);
      } catch (e) {
        // Un fallo puntual de red no debe romper la pantalla: se reintenta en
        // el siguiente tick.
      }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    tick();
    return () => { alive = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, [live.id, live.status]);

  // ── Estimación sobre los valores QUE SE VEN en el formulario ─────────────
  const runEstimate = useCallback(async () => {
    if (!form.templateId) {
      showToast('Elige una plantilla antes de estimar', 'error');
      return;
    }
    setEstimating(true);
    try {
      const data = await smsApi.estimate({
        templateId: form.templateId,
        shopId: form.shopId || undefined,
      });
      setEstimate(data || null);
      setEstimateSig(`${form.templateId}|${form.shopId}`);
    } catch (e) {
      setEstimate(null);
      setEstimateSig(null);
      showToast(apiError(e, 'No se pudo calcular la estimación'), 'error');
    } finally {
      setEstimating(false);
    }
  }, [form.templateId, form.shopId, showToast]);

  // ── Guardar ──────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!form.name.trim()) { showToast('El nombre es obligatorio', 'error'); return null; }
    if (!form.templateId) { showToast('La plantilla es obligatoria', 'error'); return null; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        templateId: form.templateId,
        shopId: form.shopId || null,
        scheduledAt: localInputToIso(form.scheduledAt),
      };
      const data = live.id
        ? await smsApi.updateCampaign(live.id, payload)
        : await smsApi.createCampaign(payload);

      setLive(data);
      setSaved({
        name: data.name || '',
        templateId: data.templateId || '',
        shopId: data.shopId || '',
        scheduledAt: isoToLocalInput(data.scheduledAt),
      });
      showToast(
        payload.scheduledAt
          ? `Campaña programada para el ${fmtWhen(payload.scheduledAt)}`
          : 'Borrador guardado',
        'success'
      );
      onSaved();
      return data;
    } catch (e) {
      showToast(apiError(e, 'No se pudo guardar la campaña'), 'error');
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, live.id, onSaved, showToast]);

  // ── Abrir el modal de envío: re-estimar sobre lo GUARDADO ────────────────
  const openSend = useCallback(async () => {
    if (!live.id) { showToast('Guarda la campaña antes de enviarla', 'error'); return; }
    if (dirty) { showToast('Tienes cambios sin guardar. Guarda antes de enviar.', 'error'); return; }

    setSendOpen(true);
    setSendEstimate(null);
    setSendLoading(true);
    try {
      // estimateCampaign() usa la plantilla y la tienda YA GUARDADAS, que es
      // exactamente lo que el backend va a despachar. Nunca confirmamos con la
      // estimación del formulario, que podría ser de otra configuración.
      const data = await smsApi.estimateCampaign(live.id);
      setSendEstimate(data || null);
    } catch (e) {
      setSendEstimate(null);
      showToast(apiError(e, 'No se pudo calcular la estimación previa al envío'), 'error');
    } finally {
      setSendLoading(false);
    }
  }, [live.id, dirty, showToast]);

  const confirmSend = useCallback(async () => {
    if (!live.id) return;
    setSending(true);
    try {
      const res = await smsApi.sendCampaign(live.id);
      setSendOpen(false);
      showToast(`Enviando a ${num(res && res.recipients)} destinatario(s)`, 'success');
      setLive((c) => ({ ...c, status: 'sending' }));
      onSaved();
    } catch (e) {
      showToast(apiError(e, 'No se pudo iniciar el envío'), 'error');
    } finally {
      setSending(false);
    }
  }, [live.id, onSaved, showToast]);

  const confirmCancel = useCallback(async () => {
    if (!live.id) return;
    setCancelling(true);
    try {
      const data = await smsApi.cancelCampaign(live.id);
      setLive(data || { ...live, status: 'cancelled' });
      setCancelOpen(false);
      showToast('Campaña cancelada', 'success');
      onSaved();
    } catch (e) {
      showToast(apiError(e, 'No se pudo cancelar la campaña'), 'error');
    } finally {
      setCancelling(false);
    }
  }, [live, onSaved, showToast]);

  const scheduledPreview = form.scheduledAt ? localInputToIso(form.scheduledAt) : null;
  const scheduledInPast = scheduledPreview ? new Date(scheduledPreview).getTime() < Date.now() : false;

  return (
    <>
      <div className="modal-bg" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="modal" style={{ width: 860, maxHeight: '92vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div className="modal-title" style={{ marginBottom: 0 }}>
              {isNew ? 'Nueva campaña SMS' : 'Editar campaña SMS'}
            </div>
            <StatusBadge status={status} />
          </div>

          {locked && (
            <div style={{ marginTop: 12, fontSize: 12, color: G.orange, lineHeight: 1.5 }}>
              {inFlight
                ? 'La campaña se está enviando: no se puede editar ni volver a enviar mientras tanto.'
                : 'La campaña ya se envió: queda en sólo lectura.'}
            </div>
          )}

          {live.lastError && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 8,
                background: G.redDim,
                border: `1px solid ${G.red}40`,
                fontSize: 12.5,
                color: G.white,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: G.red }}>Último error:</strong> {humanError(live.lastError)}
            </div>
          )}

          {/* ── Datos de la campaña ──────────────────────────────────────── */}
          <div style={{ marginTop: 18 }}>
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input
                className="form-input"
                placeholder="Recuperación de carritos — septiembre"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                disabled={locked}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plantilla SMS</label>
                <select
                  className="form-input"
                  value={form.templateId}
                  onChange={(e) => set({ templateId: e.target.value })}
                  disabled={locked}
                >
                  <option value="">— Elige una plantilla —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.active === false ? ' (inactiva)' : ''}
                    </option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <div style={{ fontSize: 11.5, color: G.orange, marginTop: 6 }}>
                    No tienes ninguna plantilla. Créala en Plantillas SMS: sin plantilla no se puede
                    crear una campaña.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tienda</label>
                <select
                  className="form-input"
                  value={form.shopId}
                  onChange={(e) => set({ shopId: e.target.value })}
                  disabled={locked}
                >
                  <option value="">Todas mis tiendas</option>
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>{s.name || s.url || s.id}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11.5, color: G.muted, marginTop: 6 }}>
                  Limita la audiencia a los compradores de esa tienda.
                </div>
              </div>
            </div>

            {/* Vista previa de la plantilla: qué se va a mandar, sin salir de aquí */}
            {selectedTemplate && (
              <div className="form-group">
                <label className="form-label">Vista previa del mensaje</label>
                <div
                  style={{
                    border: `1px solid ${G.border}`,
                    borderRadius: 10,
                    padding: 14,
                    background: G.black,
                    fontFamily: 'DM Mono, monospace',
                    fontSize: 12.5,
                    color: G.white,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                  }}
                >
                  {selectedTemplate.body || <span style={{ color: G.muted }}>(plantilla vacía)</span>}
                </div>
                <div style={{ fontSize: 11.5, color: G.muted, marginTop: 6, lineHeight: 1.5 }}>
                  {selectedTemplate.stats
                    ? `${selectedTemplate.stats.chars} caracteres · ${selectedTemplate.stats.encoding} · ${selectedTemplate.stats.segments} segmento(s)`
                    : 'Sin datos de segmentación'}
                  {' · '}
                  Las variables <span className="mono">{'{{...}}'}</span> se sustituyen por cliente, así
                  que la longitud final puede cambiar.
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Programar para (opcional)</label>
              <input
                className="form-input"
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(e) => set({ scheduledAt: e.target.value })}
                disabled={locked}
              />
              <div style={{ fontSize: 11.5, color: G.muted, marginTop: 6, lineHeight: 1.5 }}>
                {form.scheduledAt
                  ? <>Se enviará el <strong style={{ color: G.dim }}>{fmtWhen(scheduledPreview)}</strong> (zona del panel: {getAppTz()}).</>
                  : 'Vacío = no se programa; queda como borrador y se envía cuando pulses "Enviar ahora".'}
              </div>
              {scheduledInPast && (
                <div style={{ fontSize: 11.5, color: G.orange, marginTop: 5 }}>
                  Esa fecha ya pasó: el cron la recogerá en su siguiente pasada y la enviará
                  enseguida.
                </div>
              )}
            </div>
          </div>

          {/* ── Estimación ───────────────────────────────────────────────── */}
          <div style={{ marginTop: 20 }}>
            <EstimateBlock
              estimate={estimate}
              stale={stale}
              loading={estimating}
              onEstimate={runEstimate}
              canEstimate={!!form.templateId}
            />
          </div>

          {/* ── Métricas de entrega ──────────────────────────────────────── */}
          {!isNew && (
            <div style={{ marginTop: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: G.dim, marginBottom: 10 }}>
                Métricas de entrega
              </div>
              <MetricsBlock metrics={live.metrics} status={status} />
            </div>
          )}

          {/* ── Acciones ─────────────────────────────────────────────────── */}
          <div className="modal-actions" style={{ flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving || sending}>
              Cerrar
            </button>

            {!locked && (
              <button className="btn btn-ghost" onClick={save} disabled={saving}>
                {saving ? 'Guardando…' : form.scheduledAt ? 'Programar' : 'Guardar borrador'}
              </button>
            )}

            {!isNew && (status === 'scheduled' || status === 'sending') && (
              <button className="btn btn-danger" onClick={() => setCancelOpen(true)} disabled={cancelling}>
                Cancelar campaña
              </button>
            )}

            {!locked && (
              <button
                className="btn btn-gold"
                onClick={openSend}
                disabled={isNew || dirty || saving || sending}
                title={
                  isNew ? 'Guarda la campaña primero'
                    : dirty ? 'Tienes cambios sin guardar'
                      : undefined
                }
              >
                Enviar ahora
              </button>
            )}
          </div>

          {!locked && dirty && !isNew && (
            <div style={{ fontSize: 11.5, color: G.orange, textAlign: 'right', marginTop: 8 }}>
              Hay cambios sin guardar: guárdalos para poder enviar.
            </div>
          )}
        </div>
      </div>

      {sendOpen && (
        <SendConfirmModal
          campaign={live}
          estimate={sendEstimate}
          loading={sendLoading}
          busy={sending}
          onCancel={() => { if (!sending) setSendOpen(false); }}
          onConfirm={confirmSend}
        />
      )}

      {cancelOpen && (
        <CancelConfirmModal
          campaign={live}
          busy={cancelling}
          onClose={() => { if (!cancelling) setCancelOpen(false); }}
          onConfirm={confirmCancel}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sección principal
// ═══════════════════════════════════════════════════════════════════════════

export default function SmsCampaigns({ showToast }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [shops, setShops] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');   // '' = todas
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  const [visibleCols, setVisibleCols] = useState(
    () => new Set(COLS.filter((c) => !c.off).map((c) => c.key))
  );
  const [colsOpen, setColsOpen] = useState(false);
  const [dense, setDense] = useState(false);

  const [editing, setEditing] = useState(null);   // campaña en edición o null
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Carga del listado ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort, order };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const data = await smsApi.listCampaigns(params);
      setRows(Array.isArray(data && data.campaigns) ? data.campaigns : []);
      setTotal((data && data.total) || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast(apiError(e, 'No se pudieron cargar las campañas'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, order, statusFilter, search, showToast]);

  useEffect(() => { load(); }, [load]);

  // Clientes alcanzables por SMS en toda la cuenta (mismo buildAudience que el envío).
  const [reachable, setReachable] = useState(null);
  useEffect(() => {
    let alive = true;
    smsApi.audience()
      .then(d => { if (alive) setReachable(Number(d && d.reachable) || 0); })
      .catch(() => { /* sin dato no afirmamos nada: el aviso queda oculto */ });
    return () => { alive = false; };
  }, []);

  // Plantillas y tiendas para los desplegables del editor. Un fallo aquí no
  // debe tumbar la sección: el editor avisa si la lista viene vacía.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await smsApi.listTemplates({ limit: 200 });
        const list = Array.isArray(data && data.templates) ? data.templates : [];
        if (alive) setTemplates(list);
      } catch (e) {
        if (alive) setTemplates([]);
        showToast('No se pudieron cargar las plantillas SMS', 'error');
      }
      try {
        const data = await shopsApi.getAll();
        const list = Array.isArray(data) ? data : (Array.isArray(data && data.shops) ? data.shops : []);
        if (alive) setShops(list);
      } catch (e) {
        if (alive) setShops([]);
        // Sin tiendas el editor sigue funcionando (audiencia = todas), así que
        // esto no merece un toast de error a pantalla completa.
      }
    })();
    return () => { alive = false; };
  }, [showToast]);

  // Si alguna campaña está enviando, refrescamos el listado solo para verla
  // avanzar. Se para en cuanto ninguna está en vuelo.
  const anySending = useMemo(() => rows.some((c) => c && c.status === 'sending'), [rows]);
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setInterval(() => { load(); }, POLL_MS * 2);
    return () => clearInterval(t);
  }, [anySending, load]);

  // ── Interacciones de tabla ───────────────────────────────────────────────
  const toggleSort = (key) => {
    const col = COLS.find((c) => c.key === key);
    if (!col || !col.sortable) return;
    if (sort === key) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
    setPage(1);
  };

  const toggleCol = (key) => setVisibleCols((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const applySearch = () => { setPage(1); setSearch(draftSearch.trim()); };
  const resetFilters = () => {
    setDraftSearch(''); setSearch(''); setStatusFilter(''); setPage(1);
  };

  // ── Valor de celda ───────────────────────────────────────────────────────
  const cell = (c, key) => {
    const m = c.metrics || {};
    switch (key) {
      case 'name':         return c.name || '--';
      case 'templateName': return c.templateName || <span style={{ color: G.orange }}>sin plantilla</span>;
      case 'shopName':     return c.shopName || 'Todas';
      case 'recipients':   return num(c.recipients);
      case 'status':       return <StatusBadge status={c.status} />;
      case 'sent':         return num(m.sent);
      case 'delivered':    return num(m.delivered);
      case 'failed':       return (Number(m.failed) || 0) > 0
        ? <span style={{ color: G.red }}>{num(m.failed)}</span>
        : num(m.failed);
      case 'cost':         return m.attempted ? money(m.cost, m.currency) : '--';
      case 'scheduledAt':  return c.scheduledAt ? fmtWhen(c.scheduledAt) : '--';
      case 'sentAt':       return c.sentAt ? fmtWhen(c.sentAt) : '--';
      case 'createdAt':    return fmtDay(c.createdAt);
      default:             return c[key] != null ? String(c[key]) : '--';
    }
  };

  /** Valor plano para exportar (sin JSX). */
  const exportCell = (c, key) => {
    const m = c.metrics || {};
    switch (key) {
      case 'templateName': return c.templateName || '';
      case 'shopName':     return c.shopName || 'Todas';
      case 'status':       return (STATUS_META[c.status] || {}).label || c.status || '';
      case 'recipients':   return Number(c.recipients) || 0;
      case 'sent':         return Number(m.sent) || 0;
      case 'delivered':    return Number(m.delivered) || 0;
      case 'failed':       return Number(m.failed) || 0;
      case 'cost':         return m.attempted ? (Number(m.cost) || 0).toFixed(4) : '';
      case 'scheduledAt':  return c.scheduledAt ? fmtWhen(c.scheduledAt) : '';
      case 'sentAt':       return c.sentAt ? fmtWhen(c.sentAt) : '';
      case 'createdAt':    return fmtDay(c.createdAt);
      default:             return c[key] != null ? String(c[key]) : '';
    }
  };

  // ── Exportación (todo lo filtrado, no sólo la página visible) ────────────
  const exportAll = async (kind) => {
    try {
      const params = { limit: 100, sort, order };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const all = [];
      for (let p = 1; ; p++) {
        const data = await smsApi.listCampaigns({ ...params, page: p });
        const batch = Array.isArray(data && data.campaigns) ? data.campaigns : [];
        all.push(...batch);
        if (batch.length < 100 || all.length >= ((data && data.total) || 0)) break;
      }
      if (!all.length) { showToast('No hay campañas que exportar', 'error'); return; }

      const cols = COLS.filter((c) => visibleCols.has(c.key));
      const header = cols.map((c) => c.label);
      const body = all.map((c) => cols.map((col) => exportCell(c, col.key)));
      const stamp = new Date().toISOString().split('T')[0];

      if (kind === 'csv') {
        const csv = [header, ...body]
          .map((r) => r.map((v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `campanas_sms_${stamp}.csv`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Campañas SMS');
        XLSX.writeFile(wb, `campanas_sms_${stamp}.xlsx`);
      }
      showToast(`${all.length} campaña(s) exportadas`, 'success');
    } catch (e) {
      showToast(apiError(e, 'Error al exportar'), 'error');
    }
  };

  // ── Borrado ──────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await smsApi.deleteCampaign(toDelete.id);
      showToast('Campaña borrada', 'success');
      setToDelete(null);
      load();
    } catch (e) {
      showToast(apiError(e, 'No se pudo borrar la campaña'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── KPIs de la página visible ────────────────────────────────────────────
  // Sólo agregamos lo que el servidor nos ha dado en ESTA página; etiquetado
  // como tal para no dar a entender que es el total histórico.
  const kpis = useMemo(() => {
    const k = { scheduled: 0, sending: 0, delivered: 0, failed: 0, cost: 0, currency: 'EUR', anyCost: false };
    for (const c of rows) {
      if (!c) continue;
      if (c.status === 'scheduled') k.scheduled++;
      if (c.status === 'sending') k.sending++;
      const m = c.metrics || {};
      k.delivered += Number(m.delivered) || 0;
      k.failed += Number(m.failed) || 0;
      if (m.attempted) {
        k.cost += Number(m.cost) || 0;
        k.anyCost = true;
        if (m.currency) k.currency = m.currency;
      }
    }
    return k;
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const cols = COLS.filter((c) => visibleCols.has(c.key));
  const pad = dense ? '7px 12px' : undefined;
  const colSpan = cols.length + 1; // +1 por la columna de acciones

  return (
    <div>
      <NoPhonesNotice reachable={reachable} />

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Campañas</div>
          <div className="kpi-value">{num(total)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {statusFilter ? `filtradas por ${(STATUS_META[statusFilter] || {}).label || statusFilter}` : 'en total'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Programadas</div>
          <div className="kpi-value" style={{ color: kpis.scheduled ? G.blue : undefined }}>{num(kpis.scheduled)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {kpis.sending ? `${num(kpis.sending)} enviándose ahora` : 'en esta página'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Entregados</div>
          <div className="kpi-value" style={{ color: kpis.delivered ? G.green : undefined }}>{num(kpis.delivered)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {kpis.failed ? `${num(kpis.failed)} fallidos` : 'en esta página'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Coste</div>
          <div className="kpi-value">{kpis.anyCost ? money(kpis.cost, kpis.currency) : '--'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {kpis.anyCost ? 'en esta página' : 'sin envíos todavía'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Campañas SMS</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refrescar</button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setColsOpen((o) => !o)}>▤ Columnas</button>
              {colsOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 200, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                  {COLS.map((c) => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px', fontSize: 12, color: G.white, cursor: 'pointer' }}>
                      <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                      {c.label}
                    </label>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(COLS.map((c) => c.key)))}>Todas</button>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setColsOpen(false)}>Cerrar</button>
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setDense((d) => !d)}>{dense ? '↕ Normal' : '↕ Compacta'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('csv')}>↓ CSV</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('xlsx')}>↓ Excel</button>
            <button
              className="btn btn-gold btn-sm"
              onClick={() => setEditing({ status: 'draft' })}
              disabled={templates.length === 0}
              title={templates.length === 0 ? 'Necesitas al menos una plantilla SMS' : undefined}
            >
              + Nueva campaña
            </button>
          </div>
        </div>

        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="pill-group">
            <button
              className={`pill${statusFilter === '' ? ' active' : ''}`}
              onClick={() => { setStatusFilter(''); setPage(1); }}
            >
              Todas
            </button>
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                className={`pill${statusFilter === s ? ' active' : ''}`}
                onClick={() => { setStatusFilter(s); setPage(1); }}
              >
                {STATUS_META[s].label}
              </button>
            ))}
          </div>

          <div className="filters" style={{ marginTop: 12 }}>
            <input
              className="search"
              placeholder="Buscar por nombre de campaña…"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
            />
            <button className="btn btn-gold btn-sm" onClick={applySearch}>Filtrar</button>
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Resetear</button>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    style={{ cursor: c.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', textAlign: c.num ? 'right' : 'left', padding: pad }}
                    title={c.sortable ? 'Ordenar' : undefined}
                  >
                    {c.label}
                    {sort === c.key && <span style={{ color: G.gold, marginLeft: 5 }}>{order === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                ))}
                <th style={{ textAlign: 'right', whiteSpace: 'nowrap', padding: pad }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: 36, color: G.muted }}>Cargando…</td></tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={colSpan} style={{ textAlign: 'center', padding: 40 }}>
                    <div className="empty">
                      <div className="empty-icon">✉</div>
                      <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>
                        {search || statusFilter ? 'Ninguna campaña coincide con el filtro' : 'Todavía no hay campañas'}
                      </div>
                      <div style={{ fontSize: 12, color: G.muted }}>
                        {search || statusFilter
                          ? 'Prueba a quitar el filtro o a buscar otro nombre.'
                          : 'Crea una campaña, elige plantilla y estima antes de enviar nada.'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && rows.map((c) => {
                const sending = c.status === 'sending';
                const m = c.metrics || {};
                const attempted = Number(m.attempted) || 0;
                const totalRec = Number((c.stats && c.stats.total)) || Number(c.recipients) || 0;
                const pct = sending && totalRec > 0 ? Math.min(100, Math.round((attempted / totalRec) * 100)) : 0;

                return (
                  <tr key={c.id}>
                    {cols.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: pad,
                          whiteSpace: 'nowrap',
                          textAlign: col.num ? 'right' : 'left',
                          fontFamily: col.num || col.key === 'scheduledAt' || col.key === 'sentAt' || col.key === 'createdAt'
                            ? 'DM Mono, monospace'
                            : undefined,
                          color: col.key === 'name' ? G.white : undefined,
                          fontWeight: col.key === 'name' ? 600 : undefined,
                        }}
                      >
                        {col.key === 'status' && sending ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 96 }}>
                            <StatusBadge status={c.status} />
                            <div className="progress"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
                            <span style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
                              {num(attempted)}/{num(totalRec)}
                            </span>
                          </div>
                        ) : (
                          cell(c, col.key)
                        )}
                      </td>
                    ))}
                    <td style={{ padding: pad, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(c)}>
                        {c.status === 'sent' || sending ? 'Ver' : 'Abrir'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ marginLeft: 6 }}
                        onClick={() => setToDelete(c)}
                        disabled={sending}
                        title={sending ? 'No se puede borrar una campaña en curso' : undefined}
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${G.border}`, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {total === 0 ? 'Sin registros' : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} de ${num(total)}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              className="form-input"
              style={{ width: 'auto', padding: '5px 28px 5px 10px', fontSize: 11 }}
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 11, color: G.muted }}>por página</span>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            <span style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', minWidth: 62, textAlign: 'center' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      </div>

      {editing && (
        <CampaignEditor
          key={editing.id || '__nueva__'}
          campaign={editing}
          templates={templates}
          shops={shops}
          showToast={showToast}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      {toDelete && (
        <DeleteConfirmModal
          campaign={toDelete}
          busy={deleting}
          onClose={() => { if (!deleting) setToDelete(null); }}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}
